<template>
  <div class="Keypad">
    <slot name="InputBox">
      <div class="p-inputtext" style="text-align: center; font-size: larger; font-family: Reddit Mono; font-weight: bold;" :class="{ valid: isValid }">
        <span v-if="prefix || (isNumeric && !isPositive)" style="font-weight: 300;">
          {{ prefix }} {{ isPositive ? '+' : '-' }}
        </span>
        {{ valueString }}
        <span style="font-weight: 300;"
          v-html="suffix"
        ></span>
      </div>
    </slot>
    <div class="keypadButtons">
      <Button v-for="button in keys"
        :disabled="button.disabled"
        :style="button.style"
        :severity="button.severity"
        @click="button.handler"
      >
        <template v-if="button.text">
          {{ button.text }}
        </template>
        <template v-if="button.icon">
          <i :class="button.icon"></i>
        </template>
      </Button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useEventListener, watchImmediate } from "@vueuse/core";
import { roundToNearest, roundToNearestFixedRange } from "./utility.js";

const props = defineProps({
  value: {},
  range: {},
  prefix: {},
  suffix: {},
  edited: {},
  formatter: { default: () => (value) => value.toString() },
  disableInvert: {},
  disableDecimal: {},
  validator: {}
});

const emit = defineEmits([
  "ok",
  "cancel",
  "keypress",
  "formatter",
  "erase"
]);

const value = defineModel("value");
const isNumeric = computed(() => typeof value.value == "number");
const isPositive = computed(() => value.value >= 0);
const edited = defineModel("edited", { default: false });
const valueString = ref(null);

// Initializes 'valueString' and updates it when props.value changes.
watchImmediate(() => props.value, () => {
  if (edited.value)
    return;
  else if (typeof props.value == "string")
    valueString.value = props.value;
  else
    valueString.value = props.formatter(Math.abs(props.value));
});

// Updates 'value' and emits it to the parent component.
watch(valueString, () => {
  if (typeof props.value == "string")
    value.value = valueString.value;
  else
    value.value = parseFloat(valueString.value) * (isPositive.value ? 1 : -1);
});

let isValid = computed(() => {
  if (props.validator)
    return props.validator(valueString.value);
  else if (isNaN(valueString.value))
    return false;
  else if (valueString.value.endsWith("."))
    return false;
  else if (!props.range)
    return true;
  else
    return (value.value >= props.range[0] && value.value <= props.range[1]);
});

function onKeypress(char) {
  emit("keypress", char);

  // On first input, clear the existing value.
  if (!edited.value) 
    valueString.value = (char == "." ? "0" : "");
      
  edited.value = true;
  valueString.value += char;
  
  // Prevents redundant leading zeros.
  if (valueString.value.length == 2 && valueString.value[0] == "0" && valueString.value[1] != ".")
    valueString.value = valueString.value[1];
}

function erase() {
  emit("erase");
  edited.value = true;
  valueString.value = valueString.value.slice(0, -1);
  if (isNaN(parseFloat(valueString.value)))
    valueString.value = "0";
}

function invert() {
  value.value = value.value * -1;
}

function submit() {
  if (typeof props.value == "string")
    emit("ok", valueString.value);
  else
    emit("ok", value.value);
}

let disableDecimal = computed(() => {
  return props.disableDecimal || (edited.value && valueString.value.includes("."));
});

let keys = computed(() => [
  { text: "7", handler: () => onKeypress(7), severity: "secondary", style: "border-top-left-radius: 10px;" },
  { text: "8", handler: () => onKeypress(8), severity: "secondary" },
  { text: "9", handler: () => onKeypress(9), severity: "secondary" },
  { icon: "pi pi-delete-left", handler: erase, severity: "secondary", style: "border-top-right-radius: 10px;" },
  { text: "4", handler: () => onKeypress(4), severity: "secondary" },
  { text: "5", handler: () => onKeypress(5), severity: "secondary" },
  { text: "6", handler: () => onKeypress(6), severity: "secondary" },
  { text: "+/-", disabled: props.disableInvert, handler: () => invert(), severity: "secondary" },
  { text: "1", handler: () => onKeypress(1), severity: "secondary" },
  { text: "2", handler: () => onKeypress(2), severity: "secondary" },
  { text: "3", handler: () => onKeypress(3), severity: "secondary" },
  { text: "Cancel", handler: () => emit("cancel"), severity: "danger", style: "font-size: 16px; background: #ef7676; border: 0;" },
  { text: "0", handler: () => onKeypress(0), severity: "secondary", style: "grid-column: span 2; border-bottom-left-radius: 10px;" },
  { text: ".", disabled: disableDecimal.value, handler: () => onKeypress("."), severity: "secondary" },
  { text: "Ok", disabled: !isValid.value, handler: submit, style: "border-bottom-right-radius: 10px; background: var(--blue-400); border: 0;" }
]);

useEventListener(document, "keydown", onKeydown);
useEventListener(document, "copy", onCopy);
useEventListener(document, "paste", onPaste);

function onKeydown(event) {
  switch (event.key) {
    case "Escape": 
      emit("cancel");
      break;
    case "Backspace":
      erase();
      break;
    case ".":
      onKeypress(".");
      break;
    case "=":
    case "-":
      invert();
      break;
    case "Enter":
      submit();
      break;
    default:
      if (!isNaN(event.key))
        onKeypress(event.key);
      break;
  }
}

function onCopy(event) {
  event.clipboardData.setData("text/plain", valueString.value);
  event.preventDefault();
}

function onPaste(event) {
  valueString.value = (event.clipboardData || window.clipboardData).getData('text');
}
</script>

<style scoped>
.Keypad {
  min-width: 300px;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.keypadButtons{
  height: 300px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 1px;
}
.p-inputtext:not(.valid) {
  color: var(--red-600);
}
.p-button {
  display: block;
  width: 100%;
  padding: 0;
  font-size: large;
  border-radius: 0;
}
</style>